// The one "Add to visit →": what is about to be held (one line per option,
// its length and headcount), the start, the total, and — until Add is ready —
// the next step, worded from what is actually missing ("Next: pick a start
// time for Sat 26 Sept"; state/next-step-logic.ts).
//
// Always in view, at every width: a slim bar — the chosen day and start, the
// next step or the total, and the one Add — sticks to the bottom of the
// window while the panel is on screen. The summary above it stays in the
// panel's flow and keeps the lines, the total and any message, so only the
// bar's one row ever sits over the steps (a stuck summary of two rides and a
// message covered the whole "When" step on a laptop). Above 480 px the two
// read as one card while the bar is in its place. The bar comes AFTER
// everything else in the panel's flow, so it takes up its own room: scrolled
// to the end, nothing sits behind it. Its measured height (--vt-avail-dock-h,
// kept current by a ResizeObserver) is the scroll margin every control in the
// panel keeps, so a focused control is never scrolled to a place behind it.
// There is exactly one Add, so it is never announced twice.
//
// Add is never a dead end: while something is missing it stays pressable
// (aria-disabled, never disabled) and looks secondary; a press calls
// onShowNext — the panel scrolls to what is missing, focuses it and
// highlights it — and never adds anything.

import { formatDue } from "../../state/checkout-steps";
import { useEffect, useId, useLayoutEffect, useRef } from "react";
import { HoldErrorBanner } from "../booking/HoldFailure";

export type SummaryLine = {
  key: string;
  name: string;
  lengthText: string;
  guests: number;
  noun: { one: string; other: string };
  /** Unrounded display price per person (per_person) or per booking; null = unpriced. */
  price: number | null;
  basis: "per_person" | "per_item";
  currency: string;
  /** Already held in this visit at the chosen start (its guests there): shown, never held again or totalled. */
  inVisitGuests?: number | null;
};

export type PanelNotice = { tone: "info" | "error"; text: string; enquiry?: boolean };

const money = (ccy: string, n: number) => `${ccy} ${Math.round(n).toLocaleString()}`;
// A price after a promo code keeps its cents, as the checkout shows it
// (25% off ZAR 950 is ZAR 712.50, never "ZAR 713").
const moneyAfterCode = (ccy: string, cents: number) => formatDue(ccy, cents);

export function VisitSummary({
  lines,
  whenText,
  showPrices,
  payAtVenue,
  notice,
  onEnquire,
  ready,
  hint,
  announceHint,
  readyNote = null,
  phase,
  onAdd,
  onShowNext,
  afterPromo = null,
  promoCode = null,
}: {
  /**
   * The booking link's promo code on a line not held yet: its total (cents)
   * after the code, or null to show it as it is (link-promo.ts
   * pickedLineAfterLinkPromo). Display only; the checkout applies the code.
   */
  afterPromo?: ((totalCents: number) => number | null) | null;
  promoCode?: string | null;
  lines: SummaryLine[];
  whenText: string | null;
  showPrices: boolean;
  payAtVenue: boolean;
  notice: PanelNotice | null;
  onEnquire?: () => void;
  /** Add holds now (nothing is missing). */
  ready: boolean;
  /** The next step while Add is not ready ("Next: pick a day"); null when ready. */
  hint: string | null;
  /** Announce the hint (aria-live); off for "Checking availability…". */
  announceHint: boolean;
  /** Once a picked start makes Add ready, what the live region says ("Ready to add: …"); unseen. */
  readyNote?: string | null;
  phase: "idle" | "checking" | "holding";
  onAdd: () => void;
  /** A press on the not-ready Add: take the guest to what is missing. */
  onShowNext: () => void;
}) {
  const barHintId = useId();
  const errorRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (notice?.tone === "error") errorRef.current?.focus();
  }, [notice]);
  // The bar's height on the panel, for the scroll margin of its controls
  // (0 while it is not sticky), and whether it is stuck away from its own
  // place (data-stuck: it then sits over the steps and carries a shadow).
  useLayoutEffect(() => {
    const bar = barRef.current;
    const host = bar?.parentElement;
    if (!bar || !host) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const docked = bar.getClientRects().length > 0 && getComputedStyle(bar).position === "sticky";
      const rect = bar.getBoundingClientRect();
      host.style.setProperty("--vt-avail-dock-h", `${docked ? Math.ceil(rect.height) : 0}px`);
      // Stuck: a bottom-sticky bar only ever moves UP from its place in the
      // flow — just below the nearest shown element before it, plus the
      // panel's gap and its own (negative, when joined) top margin.
      let stuck = false;
      if (docked) {
        let prev = bar.previousElementSibling;
        while (prev && prev.getClientRects().length === 0) prev = prev.previousElementSibling;
        if (prev) {
          const gap = parseFloat(getComputedStyle(host).rowGap) || 0;
          const mt = parseFloat(getComputedStyle(bar).marginTop) || 0;
          stuck = rect.top < prev.getBoundingClientRect().bottom + gap + mt - 1;
        }
      }
      if (stuck) bar.setAttribute("data-stuck", "");
      else bar.removeAttribute("data-stuck");
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
    // The panel too: content above the bar that grows or shrinks moves its place.
    ro?.observe(bar);
    ro?.observe(host);
    // Capture: a scrolling drawer counts as much as the page.
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    window.addEventListener("resize", schedule);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
      host.style.removeProperty("--vt-avail-dock-h");
      bar.removeAttribute("data-stuck");
    };
  }, []);
  const busy = phase !== "idle";
  const canAdd = ready && !busy;
  // What this Add holds: a line already in the visit at this start is not.
  const holdLines = lines.filter((l) => l.inVisitGuests == null);
  const priced = showPrices && holdLines.length > 0 && holdLines.every((l) => l.price != null);
  // Summed from the UNROUNDED prices, rounded once.
  const total = priced
    ? holdLines.reduce((s, l) => s + (l.basis === "per_person" ? l.price! * l.guests : l.price!), 0)
    : null;
  const ccy = lines[0]?.currency ?? "ZAR";
  // The link's code on what this Add holds: each line's total after it, and
  // the visit's total after it when any line has one.
  const lineTotal = (l: SummaryLine) => (l.basis === "per_person" ? l.price! * l.guests : l.price!);
  const lineAfter = (l: SummaryLine): number | null =>
    afterPromo && l.price != null && l.price > 0 ? afterPromo(Math.round(lineTotal(l) * 100)) : null;
  const totalAfter = (() => {
    if (total == null || !afterPromo) return null;
    let discounted = false;
    const sum = holdLines.reduce((s, l) => {
      const after = lineAfter(l);
      if (after == null) return s + lineTotal(l);
      discounted = true;
      return s + after / 100;
    }, 0);
    return discounted ? sum : null;
  })();
  const was = (text: string) => (
    <s className="vt-promo-was">
      <span className="vt-sr-only">Was </span>
      {text}
    </s>
  );
  const withCode = promoCode ? <span className="vt-sr-only"> with {promoCode}</span> : null;
  const addLabel = phase === "holding" ? "Holding…" : phase === "checking" ? "Checking…" : "Add to visit →";
  // The next step shows while the panel is idle; an Add in flight says so on its button.
  const nextText = !busy && !ready ? (hint ?? "") : "";
  const onAddClick = () => {
    if (busy) return;
    if (ready) onAdd();
    else onShowNext();
  };
  // Not ready: pressable, secondary, aria-disabled. Running: aria-disabled and
  // greyed, still focusable, so a keyboard Add keeps its place instead of
  // dropping to <body>.
  const addClass = `vt-cta-inline vt-avail-add${
    canAdd ? " vt-cta-inline-ready" : busy ? " vt-avail-add-busy" : " vt-avail-add-pending"
  }`;
  // The hint is a polite live region: always in the DOM, so the first step
  // it names is announced too. "Checking availability…" shows without being
  // announced (the panel's own live region says it).
  // Ready after a pick: the same region says so, for the screen reader only
  // (focus stays on the start the guest picked).
  const hintEl = (
    <p id={barHintId} className="vt-avail-next" aria-live="polite">
      {nextText && !announceHint ? <span aria-hidden="true">{nextText}</span> : nextText}
      {!nextText && canAdd && readyNote ? <span className="vt-avail-sr">{readyNote}</span> : null}
    </p>
  );
  return (
    <>
      {/* In the flow; empty (nothing chosen, no message) it shows nothing (CSS). */}
      <div className="vt-avail-summary" role="group" aria-label="Your selection">
        {lines.length > 0 && (
          <ul className="vt-avail-lines">
            {lines.map((l) =>
              l.inVisitGuests != null ? (
                <li key={l.key} className="vt-avail-line vt-avail-line-invisit">
                  <span className="vt-avail-line-what">
                    {`${l.name}${
                      l.inVisitGuests > 0
                        ? ` · ${l.inVisitGuests} ${l.inVisitGuests === 1 ? l.noun.one : l.noun.other}`
                        : ""
                    } · already in your visit`}
                  </span>
                </li>
              ) : (
              <li key={l.key} className="vt-avail-line">
                <span className="vt-avail-line-what">
                  {l.name} · {l.lengthText} · {l.guests} {l.guests === 1 ? l.noun.one : l.noun.other}
                </span>
                {showPrices && (
                  <span className="vt-avail-line-price">
                    {l.price == null || l.price === 0 ? (
                      payAtVenue ? (
                        "Pay at venue"
                      ) : (
                        ""
                      )
                    ) : lineAfter(l) != null ? (
                      <>
                        {l.basis === "per_person" && l.guests > 1
                          ? `${money(l.currency, l.price)} × ${l.guests} = `
                          : ""}
                        {was(money(l.currency, lineTotal(l)))}{" "}
                        {moneyAfterCode(l.currency, lineAfter(l)!)}
                        {withCode}
                      </>
                    ) : l.basis === "per_person" && l.guests > 1 ? (
                      `${money(l.currency, l.price)} × ${l.guests} = ${money(l.currency, l.price * l.guests)}`
                    ) : (
                      money(l.currency, l.price)
                    )}
                  </span>
                )}
              </li>
              ),
            )}
          </ul>
        )}
        {holdLines.length > 1 && total != null && total > 0 && (
          <div className="vt-avail-total">
            <span>Total</span>
            <strong>
              {totalAfter != null ? (
                <>
                  {was(money(ccy, total))} {moneyAfterCode(ccy, Math.round(totalAfter * 100))}
                  {withCode}
                </>
              ) : (
                money(ccy, total)
              )}
            </strong>
          </div>
        )}
        {notice?.tone === "error" && (
          <div className="vt-avail-alert" role="alert" tabIndex={-1} ref={errorRef}>
            <HoldErrorBanner error={notice.text} errorEnquiryCta={!!notice.enquiry} onEnquire={onEnquire} />
          </div>
        )}
        {notice?.tone === "info" && (
          <div className="vt-avail-notice-msg" role="status">
            {notice.text}
          </div>
        )}
      </div>
      {/* The bar with the one Add: sticks to the bottom of the window. */}
      <div className="vt-avail-mbar" ref={barRef} role="group" aria-label="Add your visit">
        <div className="vt-avail-mbar-text">
          <span className="vt-avail-when">{whenText ?? ""}</span>
          {hintEl}
          {!nextText && total != null && total > 0 && (
            <span className="vt-avail-mbar-total">
              {holdLines.length > 1 ? "Total " : ""}
              {totalAfter != null ? (
                <>
                  {was(money(ccy, total))} <strong>{moneyAfterCode(ccy, Math.round(totalAfter * 100))}</strong>
                  {withCode}
                </>
              ) : (
                <strong>{money(ccy, total)}</strong>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          className={addClass}
          aria-disabled={!canAdd || undefined}
          aria-describedby={nextText ? barHintId : undefined}
          onClick={onAddClick}
        >
          {addLabel}
        </button>
      </div>
    </>
  );
}
